import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Cloud, Shield, Workflow } from 'lucide-react';

const Hero = ({ translations, language }) => {
  const t = translations[language];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/40 -z-10" />
      <div className="absolute top-24 right-8 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-30 -z-10" />
      <div className="absolute bottom-16 left-6 w-80 h-80 bg-cyan-100 rounded-full blur-3xl opacity-30 -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-5 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full border border-blue-100">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-blue-700">
                  {t.hero.badge}
                </span>
              </div>

              {/* 🔥 H1 CORREGIDO */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 leading-tight tracking-tight">
                Ordená tu negocio.
                <span className="block text-blue-700">
                  Recuperá el control.
                </span>
              </h1>

              {/* 🔥 DESCRIPCIÓN CORREGIDA */}
              <p className="text-xl text-slate-600 leading-relaxed">
                Desarrollamos sistemas a medida que simplifican tu trabajo,
                ordenan tus procesos y te permiten tener control en tiempo real,
                sin complicaciones.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
                <Cloud className="h-8 w-8 text-blue-600 mb-2" />
                <span className="text-sm font-semibold text-slate-700 text-center">
                  Acceso desde cualquier lugar
                </span>
              </div>
              <div className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
                <Workflow className="h-8 w-8 text-blue-600 mb-2" />
                <span className="text-sm font-semibold text-slate-700 text-center">
                  Procesos claros y ordenados
                </span>
              </div>
              <div className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-sm border border-slate-100">
                <Shield className="h-8 w-8 text-blue-600 mb-2" />
                <span className="text-sm font-semibold text-slate-700 text-center">
                  Acceso seguro
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white group"
                onClick={() => scrollToSection('contact')}
              >
                Solicitar diagnóstico
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-300 hover:border-blue-600 hover:text-blue-700"
                onClick={() => scrollToSection('projects')}
              >
                Ver soluciones
              </Button>
            </div>
          </div>

          <div className="relative lg:h-[640px]">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-[2rem] rotate-3" />
            <div className="relative h-full bg-white rounded-[2rem] shadow-2xl border border-slate-100 overflow-hidden p-6 lg:p-8">
              <div className="h-full rounded-[1.5rem] bg-slate-950 text-white p-6 sm:p-8 flex flex-col justify-between">
                <div>
                  <p className="text-sm uppercase tracking-[0.3em] text-blue-300 mb-4">
                    Solvere
                  </p>

                  {/* 🔥 PANEL DERECHO CORREGIDO */}
                  <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">
                    Sistemas pensados para funcionar de verdad
                  </h2>

                  <p className="text-slate-300 leading-relaxed max-w-lg">
                    Desarrollamos soluciones adaptadas a tu forma de trabajar,
                    enfocadas en simplificar procesos, reducir errores y mejorar
                    el control desde el primer día.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mt-8">
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <p className="text-sm text-blue-300 mb-2">Diagnóstico</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Analizamos cómo trabajás y detectamos dónde se generan problemas.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <p className="text-sm text-blue-300 mb-2">Solución</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Diseñamos un sistema claro, simple y adaptado a tu operatoria.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <p className="text-sm text-blue-300 mb-2">Implementación</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Lo ponemos en marcha y lo ajustamos en condiciones reales.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <p className="text-sm text-blue-300 mb-2">Seguimiento</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Acompañamos para que el sistema funcione cada vez mejor.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;